import { SearchInput } from '@components/search/SearchInput'
import { FilterField, TableFilterPanel } from '@components/table/filters'
import { useLocations } from '@services/common/useLocation'
import {
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import { Combobox, ComboboxOption } from '@/components/ui/combobox'
import { DatePicker } from '@/components/ui/date-picker'
import { Input } from '@/components/ui/input'

const FILTER_KEYS = [
  'status',
  'arrival_date_from',
  'arrival_date_to',
  'rent_min_gte',
  'rent_max_lte',
  'location'
] as const

interface Filters {
  status?: string
  arrival_date_from?: string
  arrival_date_to?: string
  rent_min_gte?: number
  rent_max_lte?: number
  location?: string
}

function parseFiltersFromParams(searchParams: URLSearchParams): Filters {
  return {
    status: searchParams.get('status') || undefined,
    arrival_date_from: searchParams.get('arrival_date_from') || undefined,
    arrival_date_to: searchParams.get('arrival_date_to') || undefined,
    rent_min_gte: searchParams.get('rent_min_gte')
      ? Number(searchParams.get('rent_min_gte'))
      : undefined,
    rent_max_lte: searchParams.get('rent_max_lte')
      ? Number(searchParams.get('rent_max_lte'))
      : undefined,
    location: searchParams.get('location') || undefined
  }
}

function flattenLocations(nodes: any[], parent = ''): ComboboxOption[] {
  const out: ComboboxOption[] = []
  for (const node of nodes ?? []) {
    const children = node?.children as any[] | undefined
    if (Array.isArray(children) && children.length > 0) {
      out.push(...flattenLocations(children, node.title))
    } else if (node?.value) {
      out.push({
        value: String(node.value),
        label: parent ? `${node.title}, ${parent}` : node.title
      })
    }
  }
  return out
}

interface Props {
  actionBar?: ReactElement
}

export function ApplicationsTableFilters({ actionBar }: Props) {
  const { t } = useTranslation('application')
  const { t: tLayout } = useTranslation('layout')
  const [searchParams, setSearchParams] = useSearchParams()
  const { data: locations, isLoading: locationsLoading } = useLocations()

  const [filtersUI, setFiltersUI] = useState<Filters>(() =>
    parseFiltersFromParams(searchParams)
  )
  const [filters, setFilters] = useState<Filters>(() =>
    parseFiltersFromParams(searchParams)
  )

  const locationOptions = useMemo(
    () => flattenLocations((locations as any[]) ?? []),
    [locations]
  )

  const areEqual = (a: Filters, b: Filters) =>
    JSON.stringify(a) === JSON.stringify(b)

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (areEqual(filtersUI, filters)) return
      setFilters(filtersUI)

      const params = new URLSearchParams(searchParams)
      FILTER_KEYS.forEach((key) => params.delete(key))
      params.delete('offset')

      if (filtersUI.status !== undefined) params.set('status', filtersUI.status)
      if (filtersUI.arrival_date_from)
        params.set('arrival_date_from', filtersUI.arrival_date_from)
      if (filtersUI.arrival_date_to)
        params.set('arrival_date_to', filtersUI.arrival_date_to)
      if (filtersUI.rent_min_gte !== undefined)
        params.set('rent_min_gte', String(filtersUI.rent_min_gte))
      if (filtersUI.rent_max_lte !== undefined)
        params.set('rent_max_lte', String(filtersUI.rent_max_lte))
      if (filtersUI.location) params.set('location', filtersUI.location)

      setSearchParams(params)
    }, 400)

    return () => clearTimeout(timeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersUI])

  const hasActiveFilters = useMemo(
    () => Object.values(filtersUI).some((v) => v !== undefined),
    [filtersUI]
  )

  const handleClear = useCallback(() => setFiltersUI({}), [])

  const numberOr = (v: string) => (v === '' ? undefined : Number(v))

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SearchInput />
        {actionBar}
      </div>

      <TableFilterPanel
        title={t('filters.title')}
        clearLabel={t('filters.clear')}
        hasActiveFilters={hasActiveFilters}
        onClear={handleClear}
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <FilterField label={t('form.labels.status')}>
            <Combobox
              searchable={false}
              clearable
              placeholder={t('filters.status')}
              value={filtersUI.status}
              onChange={(v) => setFiltersUI({ ...filtersUI, status: v })}
              options={[
                { label: t('form.common.active'), value: 'true' },
                { label: t('form.common.inactive'), value: 'false' }
              ]}
            />
          </FilterField>

          <FilterField label={t('form.labels.locations')}>
            <Combobox
              clearable
              loading={locationsLoading}
              placeholder={tLayout('common.select_location')}
              value={filtersUI.location}
              onChange={(v) => setFiltersUI({ ...filtersUI, location: v })}
              options={locationOptions}
            />
          </FilterField>

          <FilterField label={t('filters.arrivalRange')}>
            <div className="grid grid-cols-2 gap-2">
              <DatePicker
                clearable
                placeholder={t('filters.from')}
                max={filtersUI.arrival_date_to}
                value={filtersUI.arrival_date_from}
                onChange={(v) =>
                  setFiltersUI({ ...filtersUI, arrival_date_from: v })
                }
              />
              <DatePicker
                clearable
                placeholder={t('filters.to')}
                min={filtersUI.arrival_date_from}
                value={filtersUI.arrival_date_to}
                onChange={(v) =>
                  setFiltersUI({ ...filtersUI, arrival_date_to: v })
                }
              />
            </div>
          </FilterField>

          <FilterField label={t('form.labels.rent_range')}>
            <div className="flex items-center gap-2">
              <Input
                inputMode="numeric"
                placeholder={t('filters.from')}
                value={filtersUI.rent_min_gte ?? ''}
                onChange={(e) =>
                  setFiltersUI({
                    ...filtersUI,
                    rent_min_gte: numberOr(e.target.value)
                  })
                }
              />
              <span className="text-ink/40">–</span>
              <Input
                inputMode="numeric"
                placeholder={t('filters.to')}
                value={filtersUI.rent_max_lte ?? ''}
                onChange={(e) =>
                  setFiltersUI({
                    ...filtersUI,
                    rent_max_lte: numberOr(e.target.value)
                  })
                }
              />
            </div>
          </FilterField>
        </div>
      </TableFilterPanel>
    </div>
  )
}
